import styles from "./HomeBanner.module.css";
import Image from "next/image";
import Head from "next/head";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNetworkStatus } from "react-adaptive-hooks";
import Slider, { Settings } from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import CustomiseCta from "../CustomiseCta/CustomiseCta";

interface VideoLink {
  id: number;
  label: string;
  url: string;
  length: number;
}

interface Images {
  id: number;
  documentId: string;
  url: string;
  alternativeText: string;
}

interface BannerData {
  video_section: {
    title?: any;
    video_link: VideoLink | null;
    video_link_cta?: any[] | null;
  };
  sliders: {
    id: number;
    desktop_img: Images;
    mobile_img: Images;
    ctas?: any[] | null;
    title: string;
    button_text: string;
  }[];
}

interface HeroBannerProps {
  data: BannerData;
}

const HeroBanner = ({ data }: HeroBannerProps) => {
  const { effectiveConnectionType } = useNetworkStatus();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const updateIsMobile = () =>
      setIsMobile(window.matchMedia("(max-width: 760px)").matches);

    if (typeof window !== "undefined") {
      updateIsMobile();
      window.addEventListener("resize", updateIsMobile);
      return () => window.removeEventListener("resize", updateIsMobile);
    }
  }, []);

  const firstSliderImages = useMemo(() => {
    if (!data?.sliders?.length) return null;
    return {
      desktop: data.sliders[0].desktop_img.url,
      mobile: data.sliders[0].mobile_img.url,
    };
  }, [data]);

  const sliderSettings: Settings = useMemo(
    () => ({
      dots: false,
      infinite: data.sliders.length > 1,
      swipe: data.sliders.length > 1,
      speed: 300,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 4000,
      arrows: data.sliders.length > 1,
      pauseOnHover: true,
      pauseOnFocus: true,
      lazyLoad: "ondemand",
      cssEase: "cubic-bezier(0.645, 0.045, 0.355, 1)",
    }),
    [data.sliders.length]
  );

  const renderContent = useCallback(() => {
    if (effectiveConnectionType === "4g" && data?.video_section?.video_link) {
      return (
        <div className={styles.videoHomeContainer}>
          <div className={styles.overlay}></div>
          <div className={styles.callout}>
            {data?.video_section?.title && (
              <h1
                dangerouslySetInnerHTML={{ __html: data?.video_section?.title }}
              />
            )}
          </div>
          {data?.video_section?.video_link_cta && (
            <div className={styles.ButtonContainer}>
              <CustomiseCta ctaData={data?.video_section?.video_link_cta} />
            </div>
          )}
          <video
            src={data?.video_section?.video_link?.url}
            autoPlay
            loop
            playsInline
            muted
            className={styles.video}
            preload="metadata"
            poster={data.sliders[0].desktop_img.url}
          ></video>
        </div>
      );
    }

    return (
      <Slider {...sliderSettings} className={styles.swiperSlilk}>
        {data.sliders.map((slider, index) => (
          <div
            key={slider.id}
            className={styles.ImageAuto}
            style={{ cursor: "pointer" }}
          >
            <div className={styles.callout}>
              <h1>{slider.title}</h1>
            </div>

            {slider.ctas && Array.isArray(slider.ctas) && (
              <div className={styles.ButtonContainer}>
                {slider.ctas.map((cta, index) => (
                  <CustomiseCta key={cta.id || index} ctaData={cta} />
                ))}
              </div>
            )}

            <Image
              src={isMobile ? slider.mobile_img.url : slider.desktop_img.url}
              alt={slider.desktop_img.alternativeText || "Carousel image"}
              title={slider.desktop_img.alternativeText || "Carousel image"}
              width={isMobile ? 800 : 1920}
              height={isMobile ? 400 : 880}
              sizes={isMobile ? "100vw" : "50vw"}
              style={{ width: "100%", height: "auto", objectFit: "cover" }}
              priority={index === 0}
              fetchPriority={index === 0 ? "high" : "auto"}
              placeholder="blur"
              blurDataURL={
                isMobile
                  ? slider.mobile_img.url + "?w=10&q=10"
                  : slider.desktop_img.url + "?w=10&q=10"
              }
            />
          </div>
        ))}
      </Slider>
    );
  }, [effectiveConnectionType, data, sliderSettings, isMobile]);

  return (
    <section className={styles.homebannerheight}>
      <Head>
        {firstSliderImages && (
          <>
            <link
              rel="preload"
              as="image"
              href={firstSliderImages?.desktop}
              imageSrcSet={`${firstSliderImages?.desktop}?w=800 800w, ${firstSliderImages?.desktop}?w=1600 1600w, ${firstSliderImages?.desktop}?w=1920 1920w`}
              media="(min-width: 761px)"
              title="RBS Home Banner "
            />
            <link
              rel="preload"
              as="image"
              href={firstSliderImages?.mobile}
              imageSrcSet={`${firstSliderImages?.mobile}?w=400 400w, ${firstSliderImages?.mobile}?w=800 800w`}
              media="(max-width: 760px)"
              title="RBS Home Banner"
            />
          </>
        )}
      </Head>

      <section className={styles.heroVideo} data-section="Home Banner">
        <div className={styles.videoContainer}>{renderContent()}</div>
      </section>
    </section>
  );
};

export default HeroBanner;
